import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import routeConfiguration from '../../routeConfiguration';
import config from '../../config';
import { findOptionsForSelectFilter } from '../../util/search';
import { propTypes } from '../../util/types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { injectIntl, intlShape } from '../../util/reactIntl';
import { types as sdkTypes } from '../../util/sdkLoader';
import { ensureListing, ensureCurrentUser } from '../../util/data';
import { createResourceLocatorString } from '../../util/routes';
import { createSlug } from '../../util/urlHelpers';
import {
  Page,
  LayoutSingleColumn,
  LayoutWrapperTopbar,
  LayoutWrapperMain,
  LayoutWrapperFooter,
  Footer,
  NamedRedirect,
  AvatarMedium,
} from '../../components';
import { ProjectProposalForm } from '../../forms';
import SectionIconsMaybe from '../ProjectListingPage/SectionIconsMaybe';
import { HiLightBulb } from 'react-icons/hi';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  loadData,
  setInitialValues,
  sendProposal,
  fetchPortfolioWorks,
} from './ProjectProposalPage.duck';
import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/UI.duck';
import { TopbarContainer } from '../../containers';

import SectionDetails from './SectionDetails';

import css from './ProjectProposalPage.css';

const { UUID } = sdkTypes;

const filterConfig = config.custom.filters;

// A half-written application is kept in sessionStorage so an accidental
// navigation away does not lose it. Proposals are transactions, so there is no
// server-side draft to fall back on. Cleared once the proposal has been sent.
const proposalDraftKey = listingId => `proposal_draft_${listingId.uuid}`;

const readProposalDraft = key => {
  try {
    const raw = window.sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : {};
  } catch (e) {
    return {};
  }
};

const writeProposalDraft = (key, values) => {
  try {
    window.sessionStorage.setItem(key, JSON.stringify(values));
  } catch (e) {
    // Storage unavailable or full — the form keeps working without autosave.
  }
};

const clearProposalDraft = key => {
  try {
    window.sessionStorage.removeItem(key);
  } catch (e) {
    // Nothing to clear.
  }
};

export const ProjectProposalPageComponent = props => {
  const {
    intl,
    currentUser,
    scrollingDisabled,
    getListing,
    params,
    onSendProposal,
    onFetchPortfolioWorks,
    history,
    onManageDisableScrolling,
    sendProposalInProgress,
    portfolioWorks,
  } = props;
  const ensuredCurrentUser = ensureCurrentUser(currentUser);
  const listingId = new UUID(params.id);

  // null until read on the client, then the saved values ({} when there are
  // none). The form mounts only after that, so its initial values include the
  // draft and every later change is written back.
  const draftKey = proposalDraftKey(listingId);
  const [draft, setDraft] = useState(null);
  useEffect(() => {
    setDraft(readProposalDraft(draftKey));
  }, [draftKey]);
  const saveDraft = values => writeProposalDraft(draftKey, values);
  const currentListing = ensureListing(getListing(listingId));
  const listingTitle = currentListing.attributes.title;
  const descriptionText = currentListing.attributes.publicData.descriptionText;
  const projectProblem = currentListing.attributes.publicData.projectProblem;
  const projectIndustryExperience = currentListing.attributes.publicData.projectIndustryExperience;

  const listingClosed = !currentListing?.id || currentListing.attributes.state === 'closed';

  const isCustomer =
    currentUser && currentUser.attributes.profile.publicData.account_role === 'customer';
  const canSendProposals = !isCustomer;

  const categoryOptions = findOptionsForSelectFilter('category', filterConfig);

  const publicData = currentListing.attributes.publicData || {};
  const author = currentListing.author;
  const authorPartOfFreedomly = author?.attributes?.profile?.metadata?.partOfFreedomly;
  const showClientNotice = !!author?.id && !authorPartOfFreedomly;

  if (!canSendProposals) {
    return <NamedRedirect name="LandingPage" />;
  }

  const onSubmitProposal = values => {
    const { bestFit, similarWork, questions, portfolioWorkIds = [] } = values;
    const routes = routeConfiguration();

    // Prevent duplicate submissions using sessionStorage
    const submissionKey = `proposal_submission_${listingId.uuid}`;
    const existingSubmission = sessionStorage.getItem(submissionKey);

    if (existingSubmission) {
      const submissionData = JSON.parse(existingSubmission);
      const timeElapsed = Date.now() - submissionData.timestamp;

      // Prevent duplicate submissions within 60 seconds
      if (timeElapsed < 60000) {
        console.warn('Duplicate submission prevented');
        return Promise.resolve(); // Return resolved promise to avoid form errors
      }
    }

    // Mark as submitting
    sessionStorage.setItem(
      submissionKey,
      JSON.stringify({
        timestamp: Date.now(),
        listingId: listingId.uuid,
      })
    );

    // Get portfolio work details for the selected IDs
    const selectedPortfolioWorks = portfolioWorkIds
      .map(id => portfolioWorks.find(work => work.id.uuid === id))
      .filter(Boolean)
      .map(work => ({
        id: work.id.uuid,
        title: work.attributes.title,
        slug: createSlug(work.attributes.title),
      }));

    const protectedData = {
      listingTitle,
      bestFit,
      similarWork,
      questions,
      portfolioWorks: selectedPortfolioWorks,
    };

    onSendProposal(listingId, ensuredCurrentUser, protectedData)
      .then(txId => {
        // Clear the submission lock on success
        sessionStorage.removeItem(submissionKey);
        clearProposalDraft(draftKey);

        if (typeof window === 'object') {
          window.dataLayer.push({
            event: 'contact_author',
            content_type: 'application',
            ecommerce: {
              items: [
                {
                  item_name: currentListing.attributes.title,
                  item_id: currentListing.id.uuid,
                  price: '' + currentListing.attributes.price.amount / 100,
                  item_brand: currentListing.author.attributes.profile.displayName,
                  item_category: currentListing.attributes.publicData.category,
                  item_list_name: 'listing_page',
                },
              ],
            },
          });
        }

        // Redirect to ApplicationOrderPage
        history.push(
          createResourceLocatorString('ApplicationOrderPage', routes, { id: txId.uuid }, {})
        );
      })
      .catch(error => {
        // Clear the submission lock on error so user can retry
        sessionStorage.removeItem(submissionKey);
        // Error handling in duck file
      });
  };

  const schemaTitle = intl.formatMessage({
    id: 'ProjectProposalPage.schemaTitle',
  });

  const pageHeading = intl.formatMessage({
    id: 'ProjectProposalPage.pageHeading',
  });

  return (
    <Page title={schemaTitle} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopbarContainer />
        </LayoutWrapperTopbar>
        <LayoutWrapperMain>
          <div className={css.root}>
            <div className={css.main}>
              {listingClosed ? (
                <div className={css.closedSection}>
                  <h2 className={css.closedHeading}>
                    {intl.formatMessage({ id: 'ProjectProposalPage.closedHeading' })}
                  </h2>
                  <p className={css.closedText}>
                    {intl.formatMessage({ id: 'ProjectProposalPage.closedText' })}
                  </p>
                </div>
              ) : (
                <>
                  <div className={css.headerSection}>
                    <h1 className={css.pageHeading}>{pageHeading}</h1>
                    <p className={css.pageSubheading}>
                      {intl.formatMessage({ id: 'ProjectProposalPage.pageSubheading' })}
                    </p>
                  </div>

                  <div className={css.contentGrid}>
                    <div className={css.detailsSection}>
                      <div className={css.detailsTopRow}>
                        <h2 className={css.listingHeading}>{listingTitle}</h2>
                        <div className={css.authorSection}>
                          <AvatarMedium user={author} className={css.avatar} />
                          <div className={css.authorName}>
                            {author?.attributes?.profile?.displayName}
                          </div>
                        </div>
                      </div>
                      {showClientNotice ? (
                        <div className={css.clientNotice}>
                          <div className={css.clientNoticeHeadingRow}>
                            <HiLightBulb className={css.clientNoticeIcon} />
                            <span className={css.clientNoticeHeading}>
                              {intl.formatMessage({
                                id: 'ProjectProposalPage.clientPublishedHeading',
                              })}
                            </span>
                          </div>
                          <p className={css.clientNoticeBody}>
                            {intl.formatMessage({ id: 'ProjectProposalPage.clientPublishedBody' })}
                          </p>
                        </div>
                      ) : null}
                      <SectionIconsMaybe
                        publicData={publicData}
                        wrapperClassName={css.sectionIconsWrapperProposal}
                      />
                      <SectionDetails
                        descriptionText={descriptionText}
                        projectProblem={projectProblem}
                        projectIndustryExperience={projectIndustryExperience}
                        publicData={publicData}
                        categoryOptions={categoryOptions}
                        listingTitle={listingTitle}
                      />
                    </div>

                    <div className={css.formWrapper}>
                      {draft ? (
                        <ProjectProposalForm
                          initialValues={draft}
                          onValuesChange={saveDraft}
                          onSubmit={onSubmitProposal}
                          submitInProgress={sendProposalInProgress}
                          onManageDisableScrolling={onManageDisableScrolling}
                          currentUser={currentUser}
                          portfolioWorks={portfolioWorks}
                          onFetchPortfolioWorks={onFetchPortfolioWorks}
                        />
                      ) : null}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </LayoutWrapperMain>
        <LayoutWrapperFooter>
          <Footer />
        </LayoutWrapperFooter>
      </LayoutSingleColumn>
    </Page>
  );
};

const { bool, func, shape, string } = PropTypes;

ProjectProposalPageComponent.propTypes = {
  // from withRouter
  history: shape({
    push: func.isRequired,
  }).isRequired,
  location: shape({
    search: string,
  }).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,

  params: shape({
    id: string.isRequired,
    slug: string,
  }).isRequired,

  scrollingDisabled: bool.isRequired,
  getListing: func.isRequired,
  onSendProposal: func.isRequired,
  currentUser: propTypes.currentUser,
};

const mapStateToProps = state => {
  const { sendProposalInProgress, portfolioWorks } = state.ProjectProposalPage;
  const { currentUser } = state.user;

  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const getOwnListing = id => {
    const ref = { id, type: 'ownListing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  return {
    scrollingDisabled: isScrollingDisabled(state),
    getListing,
    getOwnListing,
    currentUser,
    sendProposalInProgress,
    portfolioWorks: portfolioWorks.map(work => getOwnListing(work.id)).filter(Boolean),
  };
};

const mapDispatchToProps = dispatch => {
  return {
    onSendProposal: (listingId, currentUser, protectedData) =>
      dispatch(sendProposal(listingId, currentUser, protectedData)),
    onManageDisableScrolling: (componentId, disableScrolling) =>
      dispatch(manageDisableScrolling(componentId, disableScrolling)),
    onFetchPortfolioWorks: () => dispatch(fetchPortfolioWorks()),
  };
};

const ProjectProposalPage = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(ProjectProposalPageComponent);

ProjectProposalPage.setInitialValues = initialValues => setInitialValues(initialValues);
ProjectProposalPage.loadData = loadData;

export default ProjectProposalPage;
